// filepath: client/src/stores/program/useProgramDashboardUIStore.js
// UI state only - no data fetching. Data is managed by TanStack Query.
import { create } from 'zustand';
import { persist, createJSONStorage, combine } from 'zustand/middleware';
import { useShallow } from 'zustand/react/shallow';

// Initial UI state ONLY - no data
const initialUIState = {
    // UI state for IndexFilters
    selected: 0,
    sortSelected: ['createdAt desc'],
    status: [], // Array for ChoiceList compatibility
    customer: '',
    dateCreated: '',
    queryValue: '',

    // Pagination UI state
    currentPage: 1,
};

// Create the store with persistence
const useProgramDashboardUIStoreBase = create(
    persist(
        combine(initialUIState, (set) => ({
            // Simple state setters
            setSelected: (selected) => set({ selected }),
            setSortSelected: (sortSelected) => set({ sortSelected }),
            setStatus: (status) => {
                const statusArray = Array.isArray(status) ? status : status ? [status] : [];
                set({
                    status: statusArray,
                    selected: statusArray.length > 0 ? 1 : 0,
                    currentPage: 1, // Reset to page 1 when filtering
                });
            },
            setCustomer: (customer) =>
                set({
                    customer,
                    currentPage: 1, // Reset to page 1 when filtering
                }),
            setDateCreated: (dateCreated) =>
                set({
                    dateCreated,
                    currentPage: 1, // Reset to page 1 when filtering
                }),
            setQueryValue: (queryValue) =>
                set({
                    queryValue,
                    currentPage: 1, // Reset to page 1 when searching
                }),
            setCurrentPage: (currentPage) => set({ currentPage }),

            // Reset all filters
            resetFilters: () => {
                set({
                    queryValue: '',
                    customer: '',
                    dateCreated: '',
                    status: [],
                    selected: 0,
                    currentPage: 1,
                });
            },

            // Reset all state
            reset: () => set(initialUIState),
        })),
        {
            name: 'program-dashboard-ui-store',
            storage: createJSONStorage(() => sessionStorage),
            version: 1,
        }
    )
);

// Auto-generated selectors
const createSelectors = (_store) => {
    let store = _store;
    store.use = {};
    for (let k of Object.keys(store.getState())) {
        store.use[k] = () => store((s) => s[k]);
    }
    return store;
};

export const useProgramDashboardUIStore = createSelectors(useProgramDashboardUIStoreBase);

// Optimized selector hooks
export const useFilterState = () =>
    useProgramDashboardUIStore(
        useShallow((state) => ({
            queryValue: state.queryValue,
            status: state.status,
            customer: state.customer,
            dateCreated: state.dateCreated,
            selected: state.selected,
            setSelected: state.setSelected,
            setStatus: state.setStatus,
            setCustomer: state.setCustomer,
            setDateCreated: state.setDateCreated,
            setQueryValue: state.setQueryValue,
            resetFilters: state.resetFilters,
        }))
    );

export const useSortState = () =>
    useProgramDashboardUIStore(
        useShallow((state) => ({
            sortSelected: state.sortSelected,
            setSortSelected: state.setSortSelected,
        }))
    );

export const usePaginationState = () =>
    useProgramDashboardUIStore(
        useShallow((state) => ({
            currentPage: state.currentPage,
            setCurrentPage: state.setCurrentPage,
        }))
    );
